<!-- eslint-disable -->
<template>
  <div class="products-crm">
    <!-- ============ HERO ============ -->
    <section class="pc-hero">
      <div class="pc-hero__glow" />
      <div class="pc-hero__row">
        <div class="pc-hero__left">
          <div class="pc-hero__icon">
            <feather-icon
              icon="PackageIcon"
              size="24"
            />
          </div>
          <div>
            <h2 class="pc-hero__title">
              Products &amp; Prices
            </h2>
            <p class="pc-hero__subtitle">
              What we sell in the CRM, mapped to the certificates operations issue in the ERP.
            </p>
          </div>
        </div>
        <div class="pc-hero__actions">
          <button
            type="button"
            class="pc-btn pc-btn--ghost"
            :disabled="loadingProducts || loadingPrices"
            @click="refresh"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'pc-spin': loadingProducts || loadingPrices }"
            />
            <span>Refresh</span>
          </button>
        </div>
      </div>
    </section>

    <!-- ============ TABS ============ -->
    <div class="pc-tabs">
      <button
        type="button"
        class="pc-tab"
        :class="{ 'pc-tab--active': activeTab === 'products' }"
        @click="setTab('products')"
      >
        <feather-icon
          icon="GridIcon"
          size="16"
        />
        <span>Products</span>
        <span class="pc-tab__badge">{{ totalProducts }}</span>
      </button>
      <button
        type="button"
        class="pc-tab"
        :class="{ 'pc-tab--active': activeTab === 'prices' }"
        @click="setTab('prices')"
      >
        <feather-icon
          icon="TagIcon"
          size="16"
        />
        <span>Price list</span>
        <span
          v-if="pricesLoaded"
          class="pc-tab__badge"
        >{{ prices.length }}</span>
      </button>
    </div>

    <!-- ==================================================== -->
    <!-- TAB A : PRODUCTS                                     -->
    <!-- ==================================================== -->
    <div v-if="activeTab === 'products'">
      <!-- Stats -->
      <div class="pc-stats">
        <div class="pc-stat">
          <div class="pc-stat__icon pc-stat__icon--primary">
            <feather-icon
              icon="PackageIcon"
              size="18"
            />
          </div>
          <div>
            <div class="pc-stat__value">
              {{ totalProducts }}
            </div>
            <div class="pc-stat__label">
              Total products
            </div>
          </div>
        </div>

        <div class="pc-stat">
          <div class="pc-stat__icon pc-stat__icon--info">
            <feather-icon
              icon="LayersIcon"
              size="18"
            />
          </div>
          <div>
            <div class="pc-stat__value">
              {{ groups.length }}
            </div>
            <div class="pc-stat__label">
              CRM product names
            </div>
          </div>
        </div>

        <div class="pc-stat">
          <div class="pc-stat__icon pc-stat__icon--success">
            <feather-icon
              icon="LinkIcon"
              size="18"
            />
          </div>
          <div>
            <div class="pc-stat__value">
              {{ linkedCount }}
            </div>
            <div class="pc-stat__label">
              Linked to an ERP certificate
            </div>
          </div>
        </div>

        <div class="pc-stat">
          <div class="pc-stat__icon pc-stat__icon--warning">
            <feather-icon
              icon="AlertTriangleIcon"
              size="18"
            />
          </div>
          <div>
            <div class="pc-stat__value">
              {{ unlinkedCount }}
            </div>
            <div class="pc-stat__label">
              Not matched yet
            </div>
          </div>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="pc-toolbar">
        <div class="pc-search">
          <feather-icon
            icon="SearchIcon"
            size="15"
            class="pc-search__icon"
          />
          <input
            v-model="search"
            type="text"
            class="pc-search__input"
            placeholder="Search by product name, regulation or certificate…"
          >
          <button
            v-if="search"
            type="button"
            class="pc-search__clear"
            title="Clear search"
            @click="search = ''"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>

        <select
          v-model="groupFilter"
          class="pc-select"
        >
          <option value="">
            All CRM products
          </option>
          <option
            v-for="g in groups"
            :key="'gf-' + g.crmName"
            :value="g.crmName"
          >
            {{ g.crmName }}
          </option>
        </select>

        <select
          v-model="actionFilter"
          class="pc-select"
        >
          <option value="">
            All action types
          </option>
          <option
            v-for="a in actionTypeOptions"
            :key="'af-' + a"
            :value="a"
          >
            {{ a }}
          </option>
        </select>

        <button
          v-if="search || groupFilter || actionFilter"
          type="button"
          class="pc-btn pc-btn--light"
          @click="clearProductFilters"
        >
          <feather-icon
            icon="XCircleIcon"
            size="16"
          />
          <span>Reset</span>
        </button>
      </div>

      <!-- Loading -->
      <div
        v-if="loadingProducts"
        class="pc-loading"
      >
        <div
          class="spinner-border text-primary"
          role="status"
        >
          <span class="sr-only">Loading…</span>
        </div>
        <p class="pc-loading__text">
          Loading products…
        </p>
      </div>

      <!-- Empty -->
      <div
        v-else-if="!filteredGroups.length"
        class="pc-empty"
      >
        <feather-icon
          icon="PackageIcon"
          size="42"
          class="pc-empty__icon"
        />
        <h4 class="pc-empty__title">
          No products found
        </h4>
        <p class="pc-empty__text">
          {{ products.length ? 'Try changing your search or filters.' : 'No CRM products have been configured yet.' }}
        </p>
      </div>

      <!-- Accordion -->
      <div
        v-else
        class="pc-groups"
      >
        <div
          v-for="group in filteredGroups"
          :key="'grp-' + group.crmName"
          class="pc-group"
          :class="{ 'pc-group--open': isExpanded(group.crmName) }"
        >
          <button
            type="button"
            class="pc-group__head"
            @click="toggleGroup(group.crmName)"
          >
            <span class="pc-group__chevron">
              <feather-icon
                :icon="isExpanded(group.crmName) ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="16"
              />
            </span>

            <span class="pc-group__name">{{ group.crmName || 'Unnamed product' }}</span>

            <span class="pc-group__count">{{ group.items.length }} item<span v-if="group.items.length !== 1">s</span></span>

            <span class="pc-group__chips">
              <span
                v-for="s in group.sectors"
                :key="group.crmName + '-sec-' + s"
                class="pc-chip pc-chip--info"
              >{{ s }}</span>
              <span
                v-if="!group.sectors || !group.sectors.length"
                class="pc-chip pc-chip--muted"
              >No sector</span>
            </span>
          </button>

          <div
            v-if="isExpanded(group.crmName)"
            class="pc-group__body"
          >
            <div class="pc-table-wrap">
              <table class="pc-table">
                <thead>
                  <tr>
                    <th>Regulation</th>
                    <th>Certificate (ERP)</th>
                    <th>Action type</th>
                    <th>Sector</th>
                    <th class="pc-table__th-right">
                      &nbsp;
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in group.items"
                    :key="'itm-' + item.id"
                  >
                    <td>
                      <span class="pc-strong">{{ item.regulation || '—' }}</span>
                    </td>
                    <td>
                      <div class="pc-cert">
                        <span>{{ item.certificateName || item.label || '—' }}</span>
                        <span
                          v-if="!item.erpCertificateId"
                          class="pc-badge pc-badge--warning"
                          title="Not matched to an ERP certificate"
                        >unlinked</span>
                      </div>
                    </td>
                    <td>
                      <span
                        class="pc-pill"
                        :class="actionClass(item.actionType)"
                      >{{ item.actionType || '—' }}</span>
                    </td>
                    <td>{{ item.sector || '—' }}</td>
                    <td class="pc-table__th-right">
                      <button
                        type="button"
                        class="pc-btn pc-btn--mini"
                        @click="openClients(item)"
                      >
                        <feather-icon
                          icon="UsersIcon"
                          size="14"
                        />
                        <span>Clients</span>
                      </button>
                      <button
                        type="button"
                        class="pc-btn pc-btn--mini"
                        title="Renewal alarms"
                        @click="openAlarms(item)"
                      >
                        <feather-icon
                          icon="BellIcon"
                          size="14"
                        />
                        <span>Alarms</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================================================== -->
    <!-- TAB B : PRICE LIST                                   -->
    <!-- ==================================================== -->
    <div v-else>
      <div class="pc-note">
        <feather-icon
          icon="InfoIcon"
          size="16"
        />
        <span>Prices are the standard list. Office-specific pricing is not configured yet.</span>
      </div>

      <div class="pc-toolbar">
        <div class="pc-search">
          <feather-icon
            icon="SearchIcon"
            size="15"
            class="pc-search__icon"
          />
          <input
            v-model="priceSearch"
            type="text"
            class="pc-search__input"
            placeholder="Search by country, scheme, activity or remarks…"
          >
          <button
            v-if="priceSearch"
            type="button"
            class="pc-search__clear"
            title="Clear search"
            @click="priceSearch = ''"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>

        <select
          v-model="countryFilter"
          class="pc-select"
        >
          <option value="">
            All countries
          </option>
          <option
            v-for="c in countryOptions"
            :key="'cf-' + c"
            :value="c"
          >
            {{ c }}
          </option>
        </select>

        <select
          v-model="schemeFilter"
          class="pc-select"
        >
          <option value="">
            All schemes
          </option>
          <option
            v-for="s in schemeOptions"
            :key="'sf-' + s"
            :value="s"
          >
            {{ s }}
          </option>
        </select>

        <button
          v-if="priceSearch || countryFilter || schemeFilter"
          type="button"
          class="pc-btn pc-btn--light"
          @click="clearPriceFilters"
        >
          <feather-icon
            icon="XCircleIcon"
            size="16"
          />
          <span>Reset</span>
        </button>
      </div>

      <div
        v-if="loadingPrices"
        class="pc-loading"
      >
        <div
          class="spinner-border text-primary"
          role="status"
        >
          <span class="sr-only">Loading…</span>
        </div>
        <p class="pc-loading__text">
          Loading price list…
        </p>
      </div>

      <div
        v-else-if="!filteredPrices.length"
        class="pc-empty"
      >
        <feather-icon
          icon="TagIcon"
          size="42"
          class="pc-empty__icon"
        />
        <h4 class="pc-empty__title">
          No prices found
        </h4>
        <p class="pc-empty__text">
          {{ prices.length ? 'Try changing your search or filters.' : 'The service price list is empty.' }}
        </p>
      </div>

      <div
        v-else
        class="pc-card"
      >
        <div class="pc-table-wrap">
          <table class="pc-table">
            <thead>
              <tr>
                <th>Country</th>
                <th>Scheme</th>
                <th>Activity / Item</th>
                <th class="pc-table__th-right">
                  Amount
                </th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in filteredPrices"
                :key="'pr-' + row.kind + '-' + row.id"
              >
                <td>{{ row.country || '—' }}</td>
                <td>
                  <span class="pc-strong">{{ row.scheme || '—' }}</span>
                </td>
                <td>
                  <div class="pc-cert">
                    <span>{{ activityLabel(row) }}</span>
                    <span
                      v-if="row.kind === 'catalog'"
                      class="pc-badge pc-badge--info"
                    >catalog</span>
                  </div>
                </td>
                <td class="pc-table__th-right pc-mono">
                  {{ formatMoney(row.amount, row.currency) }}
                </td>
                <td class="pc-muted">
                  {{ row.remarks || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ==================================================== -->
    <!-- CLIENTS MODAL                                        -->
    <!-- ==================================================== -->
    <div
      v-if="clientsModal"
      class="pc-modal"
    >
      <div
        class="pc-modal__backdrop"
        @click="closeClients"
      />
      <div class="pc-modal__dialog">
        <div class="pc-modal__head">
          <div>
            <h5 class="pc-modal__title">
              Clients using this product
            </h5>
            <p class="pc-modal__sub">
              {{ clientsProductLabel }}
            </p>
          </div>
          <button
            type="button"
            class="pc-modal__close"
            @click="closeClients"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </button>
        </div>

        <div class="pc-modal__body">
          <div
            v-if="clientsLoading"
            class="pc-loading"
          >
            <div
              class="spinner-border text-primary"
              role="status"
            >
              <span class="sr-only">Loading…</span>
            </div>
            <p class="pc-loading__text">
              Loading clients…
            </p>
          </div>

          <div
            v-else-if="!clients.length"
            class="pc-empty pc-empty--sm"
          >
            <feather-icon
              icon="UsersIcon"
              size="34"
              class="pc-empty__icon"
            />
            <h4 class="pc-empty__title">
              No clients yet
            </h4>
            <p class="pc-empty__text">
              Nobody has bought this product so far.
            </p>
          </div>

          <div v-if="modalSalespeople.length" class="pc-modal-sales">
            <feather-icon icon="UsersIcon" size="14" />
            <span>Salespeople on this product:</span>
            <span
              v-for="(sp, si) in modalSalespeople"
              :key="'msp-' + si"
              class="pc-pill pc-pill--primary pc-pill--sm"
            >{{ sp }}</span>
          </div>

          <div
            v-else-if="false"
            class="pc-empty"
          />

          <div
            v-if="!clientsLoading"
            class="pc-table-wrap"
          >
            <table class="pc-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Client</th>
                  <th>Default salesperson</th>
                  <th>Working on it</th>
                  <th class="pc-table__th-right">
                    Certificates
                  </th>
                  <th>Last issued</th>
                  <th>Next expiry</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(c, ci) in clients"
                  :key="'cl-' + ci + '-' + (c.clientId || c.companyId)"
                >
                  <td>
                    <span class="pc-strong">{{ c.companyName || '—' }}</span>
                  </td>
                  <td>
                    <div>{{ c.clientName || '—' }}</div>
                    <small v-if="c.email" class="pc-muted">{{ c.email }}</small>
                  </td>
                  <td>
                    <span v-if="c.accountOwner" class="pc-pill pc-pill--info">{{ c.accountOwner }}</span>
                    <span v-else class="pc-muted">unassigned</span>
                  </td>
                  <td>
                    <span
                      v-for="(sp, si) in (c.salespeople || [])"
                      :key="'sp-' + ci + '-' + si"
                      class="pc-pill pc-pill--primary pc-pill--sm"
                    >{{ sp }}</span>
                    <span v-if="!(c.salespeople || []).length" class="pc-muted">—</span>
                    <small v-if="c.openOpportunities" class="pc-muted"> ({{ c.openOpportunities }} open)</small>
                  </td>
                  <td class="pc-table__th-right">
                    <span class="pc-pill pc-pill--primary">{{ c.count || 0 }}</span>
                  </td>
                  <td>{{ formatDate(c.lastIssued) }}</td>
                  <td>{{ formatDate(c.nextExpiry) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="pc-modal__foot">
          <button
            type="button"
            class="pc-btn pc-btn--light"
            @click="closeClients"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- ==================================================== -->
    <!-- RENEWAL ALARMS MODAL                                 -->
    <!-- ==================================================== -->
    <RenewalAlarmsModal
      :visible="alarmsModal"
      :product-id="alarmsProductId"
      :product-label="alarmsProductLabel"
      @close="closeAlarms"
      @notify="onAlarmsNotify"
    />
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import RenewalAlarmsModal from './RenewalAlarmsModal.vue'

const DEFAULT_ACTIONS = ['New', 'Renew', 'Extend', 'Survey']

export default {
  name: 'ProductsCRM',
  components: { RenewalAlarmsModal },
  data() {
    return {
      activeTab: 'products',

      // products tab
      loadingProducts: false,
      products: [],
      groups: [],
      productCount: 0,
      search: '',
      groupFilter: '',
      actionFilter: '',
      expanded: [],

      // prices tab
      loadingPrices: false,
      pricesLoaded: false,
      prices: [],
      priceSearch: '',
      countryFilter: '',
      schemeFilter: '',

      // clients modal
      clientsModal: false,
      modalSalespeople: [],
      clientsLoading: false,
      clients: [],
      clientsProduct: null,

      // renewal alarms modal (Karim, 17-18 Sep)
      alarmsModal: false,
      alarmsProductId: null,
      alarmsProductLabel: '',
    }
  },
  computed: {
    totalProducts() {
      if (this.productCount) return this.productCount
      return this.products.length
    },
    linkedCount() {
      return this.products.filter(p => !!p.erpCertificateId).length
    },
    unlinkedCount() {
      return this.products.length - this.linkedCount
    },
    actionTypeOptions() {
      const set = {}
      DEFAULT_ACTIONS.forEach(a => { set[a] = true })
      this.products.forEach(p => {
        if (p && p.actionType) set[String(p.actionType)] = true
      })
      return Object.keys(set)
    },
    filteredGroups() {
      const q = String(this.search || '').trim().toLowerCase()
      const gf = this.groupFilter
      const af = this.actionFilter
      const out = []

      this.groups.forEach(g => {
        if (gf && g.crmName !== gf) return
        const items = (g.items || []).filter(item => {
          if (af && String(item.actionType || '') !== af) return false
          if (!q) return true
          const hay = [
            g.crmName,
            item.crmName,
            item.regulation,
            item.certificateName,
            item.label,
            item.sector,
          ].filter(Boolean).join(' ').toLowerCase()
          return hay.indexOf(q) !== -1
        })
        if (!items.length) return
        out.push({
          crmName: g.crmName,
          sectors: g.sectors || [],
          actionTypes: g.actionTypes || [],
          items: items,
        })
      })

      return out
    },
    countryOptions() {
      return this.uniqueValues(this.prices, 'country')
    },
    schemeOptions() {
      return this.uniqueValues(this.prices, 'scheme')
    },
    filteredPrices() {
      const q = String(this.priceSearch || '').trim().toLowerCase()
      return this.prices.filter(row => {
        if (this.countryFilter && String(row.country || '') !== this.countryFilter) return false
        if (this.schemeFilter && String(row.scheme || '') !== this.schemeFilter) return false
        if (!q) return true
        const hay = [
          row.country,
          row.scheme,
          row.activity,
          row.name,
          row.remarks,
        ].filter(Boolean).join(' ').toLowerCase()
        return hay.indexOf(q) !== -1
      })
    },
    clientsProductLabel() {
      const p = this.clientsProduct
      if (!p) return ''
      const bits = [p.crmName, p.regulation, p.certificateName || p.label, p.actionType]
      return bits.filter(Boolean).join(' • ')
    },
  },
  created() {
    this.fetchProducts()
  },
  methods: {
    setTab(tab) {
      this.activeTab = tab
      if (tab === 'prices' && !this.pricesLoaded && !this.loadingPrices) {
        this.fetchPrices()
      }
    },
    refresh() {
      if (this.activeTab === 'products') {
        this.fetchProducts()
      } else {
        this.fetchPrices()
      }
    },
    clearProductFilters() {
      this.search = ''
      this.groupFilter = ''
      this.actionFilter = ''
    },
    clearPriceFilters() {
      this.priceSearch = ''
      this.countryFilter = ''
      this.schemeFilter = ''
    },
    isExpanded(name) {
      return this.expanded.indexOf(name) !== -1
    },
    toggleGroup(name) {
      const idx = this.expanded.indexOf(name)
      if (idx === -1) {
        this.expanded.push(name)
      } else {
        this.expanded.splice(idx, 1)
      }
    },

    // ---------- data ----------
    async fetchProducts() {
      this.loadingProducts = true
      try {
        const res = await axios.get('crm/products?group=1')
        if (res && res.status === 200 && res.data && res.data.payload) {
          const body = decryptData(res.data.payload)
          const data = (body && body.data) || {}
          this.products = Array.isArray(data.products) ? data.products : []
          this.productCount = Number(data.count) || this.products.length
          let groups = Array.isArray(data.groups) ? data.groups : []
          if (!groups.length && this.products.length) {
            groups = this.buildGroups(this.products)
          }
          this.groups = groups
          if (this.groups.length === 1) {
            this.expanded = [this.groups[0].crmName]
          }
        }
      } catch (e) {
        this.showError(e, 'Failed to load products')
      } finally {
        this.loadingProducts = false
      }
    },
    async fetchPrices() {
      this.loadingPrices = true
      try {
        const res = await axios.get('crm/service-prices')
        if (res && res.status === 200 && res.data && res.data.payload) {
          const body = decryptData(res.data.payload)
          const data = (body && body.data) || []
          this.prices = Array.isArray(data) ? data : []
          this.pricesLoaded = true
        }
      } catch (e) {
        this.showError(e, 'Failed to load the price list')
      } finally {
        this.loadingPrices = false
      }
    },
    async openClients(item) {
      this.clientsProduct = item
      this.clients = []
      this.clientsModal = true
      this.clientsLoading = true
      try {
        const res = await axios.get(`crm/products/${item.id}/clients`)
        if (res && res.status === 200 && res.data && res.data.payload) {
          const body = decryptData(res.data.payload)
          const data = (body && body.data) || {}
          this.clients = Array.isArray(data.clients) ? data.clients : []
          this.modalSalespeople = Array.isArray(data.salespeople) ? data.salespeople : []
          if (data.product) this.clientsProduct = data.product
        }
      } catch (e) {
        this.showError(e, 'Failed to load clients for this product')
      } finally {
        this.clientsLoading = false
      }
    },
    closeClients() {
      this.clientsModal = false
      this.clientsLoading = false
      this.clients = []
      this.clientsProduct = null
    },

    /** Karim, 17-18 Sep: renewal alarms settings, per product. */
    openAlarms(item) {
      this.alarmsProductId = item.id
      this.alarmsProductLabel = item.certificateName || item.label || item.regulation || `Product #${item.id}`
      this.alarmsModal = true
    },
    closeAlarms() {
      this.alarmsModal = false
    },
    onAlarmsNotify({ text, variant }) {
      this.$toast({
        component: ToastificationContent,
        props: { title: text, icon: variant === 'danger' ? 'AlertCircleIcon' : 'CheckIcon', variant: variant || 'success' },
      })
    },

    // ---------- helpers ----------
    buildGroups(list) {
      const map = {}
      const order = []
      list.forEach(p => {
        const key = p.crmName || 'Unnamed product'
        if (!map[key]) {
          map[key] = { crmName: key, items: [], sectors: [], actionTypes: [] }
          order.push(key)
        }
        map[key].items.push(p)
        if (p.sector && map[key].sectors.indexOf(p.sector) === -1) map[key].sectors.push(p.sector)
        if (p.actionType && map[key].actionTypes.indexOf(p.actionType) === -1) map[key].actionTypes.push(p.actionType)
      })
      return order.map(k => map[k])
    },
    uniqueValues(list, key) {
      const seen = {}
      const out = []
      list.forEach(row => {
        const v = row && row[key]
        if (v === null || v === undefined || v === '') return
        const s = String(v)
        if (seen[s]) return
        seen[s] = true
        out.push(s)
      })
      return out.sort()
    },
    activityLabel(row) {
      if (row.kind === 'catalog') return row.name || row.activity || '—'
      return row.activity || row.name || '—'
    },
    formatMoney(amount, currency) {
      if (amount === null || amount === undefined || amount === '') return '—'
      const n = Number(amount)
      if (isNaN(n)) return String(amount)
      const formatted = n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return currency ? `${formatted} ${currency}` : formatted
    },
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(String(value).replace(' ', 'T'))
      if (isNaN(d.getTime())) return String(value)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      return `${day}/${month}/${d.getFullYear()}`
    },
    actionClass(type) {
      const t = String(type || '').toLowerCase()
      if (t === 'new') return 'pc-pill--success'
      if (t === 'renew') return 'pc-pill--primary'
      if (t === 'extend') return 'pc-pill--info'
      if (t === 'survey') return 'pc-pill--warning'
      return 'pc-pill--muted'
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error && error.response && error.response.data && error.response.data.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload && payload.message ? payload.message : payload
          if (typeof msg === 'string') message = msg
        }
      } catch (e) {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #2563eb;
$danger: #ea5455;
$warning: #3b82f6;
$info: #00cfe8;
$success: #28c76f;
$muted: #6e6b7b;
$radius: 0.6rem;
$shadow: 0 2px 8px rgba(34, 41, 47, 0.08);

.products-crm {
  padding-bottom: 2rem;
}

/* ---------- HERO ---------- */
.pc-hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #2563eb, #9e95f5);
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
  color: #fff;

  &__glow {
    position: absolute;
    top: -60px;
    right: -60px;
    bottom: auto;
    left: auto;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    pointer-events: none;
  }

  &__row {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 0.9rem;
  }

  &__icon {
    width: 48px;
    height: 48px;
    flex: 0 0 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: $radius;
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
  }

  &__title {
    margin: 0;
    font-size: 1.45rem;
    font-weight: 600;
    color: #fff;
  }

  &__subtitle {
    margin: 0.15rem 0 0;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.85);
  }

  &__actions {
    display: flex;
    gap: 0.5rem;
  }
}

/* ---------- BUTTONS ---------- */
.pc-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid transparent;
  border-radius: $radius;
  padding: 0.5rem 0.9rem;
  font-size: 0.86rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #fff;
  color: $muted;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &--ghost {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.35);
    color: #fff;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.28);
    }
  }

  &--light {
    background: #f8f8f8;
    border-color: #ebe9f1;
    color: $muted;

    &:hover:not(:disabled) {
      background: #efeefc;
      color: $primary;
    }
  }

  &--mini {
    padding: 0.28rem 0.6rem;
    font-size: 0.76rem;
    background: rgba(37, 99, 235, 0.1);
    color: $primary;
    border-color: transparent;

    &:hover:not(:disabled) {
      background: $primary;
      color: #fff;
    }
  }
}

.pc-spin {
  animation: pc-rotate 1s linear infinite;
}

@keyframes pc-rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ---------- TABS ---------- */
.pc-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.pc-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 0.55rem 1rem;
  font-size: 0.88rem;
  font-weight: 500;
  color: $muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: $primary;
    border-color: rgba(37, 99, 235, 0.4);
  }

  &--active {
    background: $primary;
    border-color: $primary;
    color: #fff;

    &:hover {
      color: #fff;
    }
  }

  &__badge {
    background: rgba(37, 99, 235, 0.12);
    color: $primary;
    border-radius: 10rem;
    padding: 0.05rem 0.45rem;
    font-size: 0.72rem;
    font-weight: 600;
  }

  &--active &__badge {
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
  }
}

/* ---------- STATS ---------- */
.pc-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.pc-stat {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 1rem 1.1rem;

  &__icon {
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: $radius;

    &--primary { background: rgba(37, 99, 235, 0.12); color: $primary; }
    &--info { background: rgba(0, 207, 232, 0.12); color: $info; }
    &--success { background: rgba(40, 199, 111, 0.12); color: $success; }
    &--warning { background: rgba(59, 130, 246, 0.12); color: $warning; }
    &--danger { background: rgba(234, 84, 85, 0.12); color: $danger; }
  }

  &__value {
    font-size: 1.35rem;
    font-weight: 600;
    line-height: 1.1;
    color: #5e5873;
  }

  &__label {
    font-size: 0.8rem;
    color: $muted;
  }
}

/* ---------- TOOLBAR ---------- */
.pc-toolbar {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
}

.pc-search {
  position: relative;
  flex: 1 1 280px;
  min-width: 220px;

  &__icon {
    position: absolute;
    top: 50%;
    left: 0.75rem;
    right: auto;
    bottom: auto;
    transform: translateY(-50%);
    color: $muted;
    pointer-events: none;
  }

  &__input {
    width: 100%;
    border: 1px solid #d8d6de;
    border-radius: $radius;
    padding: 0.5rem 2rem 0.5rem 2.25rem;
    font-size: 0.86rem;
    color: #6e6b7b;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      border-color: $primary;
      box-shadow: 0 3px 10px 0 rgba(37, 99, 235, 0.1);
    }

    &::placeholder {
      color: #b9b9c3;
    }
  }

  &__clear {
    position: absolute;
    top: 50%;
    right: 0.6rem;
    left: auto;
    bottom: auto;
    transform: translateY(-50%);
    background: transparent;
    border: 0;
    color: $muted;
    cursor: pointer;
    padding: 0;
    line-height: 1;

    &:hover { color: $danger; }
  }
}

.pc-select {
  border: 1px solid #d8d6de;
  border-radius: $radius;
  padding: 0.5rem 0.75rem;
  font-size: 0.86rem;
  color: #6e6b7b;
  background: #fff;
  outline: none;
  min-width: 175px;
  cursor: pointer;

  &:focus {
    border-color: $primary;
    box-shadow: 0 3px 10px 0 rgba(37, 99, 235, 0.1);
  }
}

/* ---------- NOTE ---------- */
.pc-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 207, 232, 0.1);
  color: #0aa2b8;
  border-left: 3px solid $info;
  border-radius: $radius;
  padding: 0.7rem 1rem;
  font-size: 0.85rem;
  margin-bottom: 1rem;
}

/* ---------- GROUPS / ACCORDION ---------- */
.pc-groups {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pc-group {
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;

  &--open {
    box-shadow: 0 4px 16px rgba(37, 99, 235, 0.16);
  }

  &__head {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: transparent;
    border: 0;
    padding: 0.9rem 1.1rem;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(37, 99, 235, 0.04);
    }
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    flex: 0 0 26px;
    border-radius: 0.35rem;
    background: rgba(37, 99, 235, 0.12);
    color: $primary;
  }

  &__name {
    font-size: 0.95rem;
    font-weight: 600;
    color: #5e5873;
    flex: 0 1 auto;
  }

  &__count {
    font-size: 0.75rem;
    font-weight: 600;
    color: $primary;
    background: rgba(37, 99, 235, 0.12);
    border-radius: 10rem;
    padding: 0.1rem 0.55rem;
    white-space: nowrap;
  }

  &__chips {
    margin-left: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    justify-content: flex-end;
  }

  &__body {
    border-top: 1px solid #f1f0f7;
    padding: 0.35rem 0.6rem 0.6rem;
  }
}

/* ---------- CHIPS / PILLS / BADGES ---------- */
.pc-chip {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 500;
  border-radius: 10rem;
  padding: 0.12rem 0.55rem;
  white-space: nowrap;

  &--info { background: rgba(0, 207, 232, 0.12); color: $info; }
  &--muted { background: #f2f2f4; color: $muted; }
}

.pc-pill {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 10rem;
  padding: 0.14rem 0.6rem;
  white-space: nowrap;

  &--primary { background: rgba(37, 99, 235, 0.12); color: $primary; }
  &--info { background: rgba(0, 207, 232, 0.12); color: $info; }
  &--success { background: rgba(40, 199, 111, 0.12); color: $success; }
  &--warning { background: rgba(59, 130, 246, 0.12); color: $warning; }
  &--danger { background: rgba(234, 84, 85, 0.12); color: $danger; }
  &--muted { background: #f2f2f4; color: $muted; }
}

.pc-badge {
  display: inline-block;
  font-size: 0.66rem;
  font-weight: 600;
  border-radius: 0.28rem;
  padding: 0.08rem 0.4rem;
  text-transform: lowercase;
  cursor: help;

  &--warning { background: rgba(59, 130, 246, 0.16); color: $warning; }
  &--info { background: rgba(0, 207, 232, 0.14); color: $info; }
}

/* ---------- TABLES ---------- */
.pc-card {
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 0.5rem;
}

.pc-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.pc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;

  thead th {
    text-align: left;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-size: 0.7rem;
    font-weight: 600;
    color: $muted;
    background: #f8f8fb;
    padding: 0.6rem 0.75rem;
    white-space: nowrap;
    border-bottom: 1px solid #f1f0f7;
  }

  tbody td {
    padding: 0.6rem 0.75rem;
    color: $muted;
    border-bottom: 1px solid #f6f6f9;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover td {
    background: rgba(37, 99, 235, 0.03);
  }

  &__th-right {
    text-align: right;
  }
}

.pc-cert {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.pc-strong {
  font-weight: 600;
  color: #5e5873;
}

.pc-muted {
  color: #b9b9c3;
}

.pc-mono {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.78rem;
}

/* ---------- LOADING / EMPTY ---------- */
.pc-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;

  &__text {
    margin: 0.75rem 0 0;
    font-size: 0.85rem;
    color: $muted;
  }
}

.pc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 1.5rem;
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;

  &--sm {
    padding: 2rem 1rem;
    box-shadow: none;
  }

  &__icon {
    color: #d8d6de;
    margin-bottom: 0.75rem;
  }

  &__title {
    margin: 0 0 0.25rem;
    font-size: 1rem;
    font-weight: 600;
    color: #5e5873;
  }

  &__text {
    margin: 0;
    font-size: 0.85rem;
    color: $muted;
  }
}

/* ---------- MODAL ---------- */
.pc-modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1090;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  &__backdrop {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(34, 41, 47, 0.5);
  }

  &__dialog {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 640px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: $radius;
    box-shadow: 0 8px 30px rgba(34, 41, 47, 0.24);
    overflow: hidden;
  }

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #f1f0f7;
  }

  &__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #5e5873;
  }

  &__sub {
    margin: 0.15rem 0 0;
    font-size: 0.8rem;
    color: $muted;
  }

  &__close {
    background: transparent;
    border: 0;
    color: $muted;
    cursor: pointer;
    padding: 0;
    line-height: 1;

    &:hover { color: $danger; }
  }

  &__body {
    padding: 0.75rem 1rem;
    overflow-y: auto;
  }

  &__foot {
    display: flex;
    justify-content: flex-end;
    padding: 0.75rem 1.25rem;
    border-top: 1px solid #f1f0f7;
  }
}
.pc-modal-sales {
  display: flex; align-items: center; flex-wrap: wrap; gap: .3rem;
  font-size: .78rem; color: #6e6b7b; margin-bottom: .6rem;
  padding: .4rem .6rem; background: #f8f8fb; border-radius: .4rem;
}
.pc-pill--sm { font-size: .68rem; padding: .05rem .4rem; }
.pc-pill--info { background: rgba(0,207,232,.14); color: #00a5b5; }
.pc-muted { color: #b9b9c3; font-size: .74rem; }
</style>
